import { type FC, useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import copy from "copy-to-clipboard";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  useDeletePromocodesMutation,
  usePromocodesQuery,
  type PromocodeListNode,
} from "@/partner/entities/promocodes";
import { formatCurrency } from "@/partner/features/products";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants/routes";
import { interpolationFormat } from "@/partner/shared/lib";
import { buildPromocodesFilter } from "../model/buildPromocodesFilter";

interface PromocodesListProps {
  onEdit: (id: string) => void;
}

export const PromocodesList: FC<PromocodesListProps> = ({ onEdit }) => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useDeviceBreakpoint("md");
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter,
  } = useFilter();

  // référence: PromocodeList.js useScrollToTop([page, rowsPerPage]) — у нас
  // нет аналога хука (référence: ProductsList.tsx:33-37), скроллим вручную.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page, rowsPerPage]);

  const { data, loading } = usePromocodesQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      page,
      rowsPerPage,
      filter: buildPromocodesFilter(filter),
    },
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
  });

  // Référence: selected — объект {id: true}; select-all — тумблер (клик при
  // полном выделении снимает всё).
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const nodes: PromocodeListNode[] = data?.promocodes?.nodes ?? [];
  const count = data?.promocodes?.count ?? 0;
  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const numSelected = selectedIds.length;

  const [deletePromocodes, { loading: deleting }] = useDeletePromocodesMutation(
    {
      variables: { ids: selectedIds },
      refetchQueries: ["PromocodesList"],
      onCompleted: () => {
        setSelected({});
        enqueueSnackbar(t("promocodes.list.deleted"), { variant: "success" });
      },
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    }
  );

  const handleToggleSelectAll = () => {
    if (numSelected === nodes.length) {
      setSelected({});
    } else {
      setSelected(
        Object.fromEntries(nodes.map((node) => [node.id, true] as const))
      );
    }
  };

  const handleToggleSelectOne = (id: string) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card elevation={0}>
      <Collapse in={numSelected > 0}>
        <AppBar position="static" color="default" elevation={0}>
          <Toolbar>
            <Typography sx={{ mr: 2 }}>
              {t("promocodes.list.selected", { count: numSelected })}
            </Typography>
            <Divider
              orientation="vertical"
              light
              variant="middle"
              sx={{ height: "2em" }}
            />
            <Button
              sx={{ ml: 2 }}
              color="inherit"
              disabled={deleting}
              onClick={() => deletePromocodes()}
            >
              {t("promocodes.list.delete")}
            </Button>
            <Divider
              orientation="vertical"
              light
              variant="middle"
              sx={{ height: "2em" }}
            />
            <Tooltip title={t("promocodes.list.close")}>
              <IconButton
                sx={{ ml: "auto" }}
                edge="end"
                size="large"
                onClick={() => setSelected({})}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>
        <Divider />
      </Collapse>

      {count === 0 ? (
        <EmptyListCard title={t("promocodes.list.empty")} />
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  color="default"
                  indeterminate={numSelected > 0 && numSelected < nodes.length}
                  checked={nodes.length > 0 && numSelected === nodes.length}
                  onChange={handleToggleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ width: "auto" }}>
                {t("promocodes.list.titleColumn")}
              </TableCell>
              {!isMobile && (
                <>
                  <TableCell sx={{ width: "auto" }}>
                    {t("promocodes.list.codeColumn")}
                  </TableCell>
                  <TableCell>
                    {t("promocodes.list.activationsColumn")}
                  </TableCell>
                  <TableCell>{t("promocodes.list.revenueColumn")}</TableCell>
                  <TableCell align="right" sx={{ width: 160 }}>
                    {t("promocodes.list.createdAtColumn")}
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {nodes.map((row) => (
              <TableRow key={row.id} selected={Boolean(selected[row.id])}>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="default"
                    checked={Boolean(selected[row.id])}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => handleToggleSelectOne(row.id)}
                  />
                </TableCell>
                <TableCell>
                  <ItemTitleLink
                    to={`${PagePaths.promocodes}?tab=edit&id=${row.id}`}
                    onOpen={() => onEdit(row.id)}
                    color="textPrimary"
                    variant="h6"
                  >
                    {row.title}
                  </ItemTitleLink>
                  {row.promocodeCampaign && (
                    <Typography color="textSecondary" variant="body2">
                      {row.promocodeCampaign.title}
                    </Typography>
                  )}
                </TableCell>
                {!isMobile && (
                  <>
                    <TableCell>
                      <Chip
                        label={row.code}
                        clickable
                        onClick={() => {
                          copy(row.code);
                          enqueueSnackbar(t("promocodes.list.codeCopied"), {
                            variant: "success",
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography>{row.activations}</Typography>
                    </TableCell>
                    <TableCell>
                      {formatCurrency(
                        row.totalRevenue.value,
                        row.totalRevenue.currency
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ width: 160 }}>
                      <Typography variant="body2" color="textPrimary" noWrap>
                        {interpolationFormat(
                          row.createdAt,
                          "short",
                          i18n.language
                        )}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" noWrap>
                        {interpolationFormat(
                          row.createdAt,
                          "time",
                          i18n.language
                        )}
                      </Typography>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Card>
  );
};
